<template>
	<article class="d-search-page-custom">
		<h1 v-if="title" class="sr-only" v-html="title"></h1>
		<LimboSearch
			v-slot="{ action, state }"
			:search-filters="filters"
			:extra-parameters="{
				searchId,
				pageId,
				url,
			}"
			:config="{
				enableLiveSearch: true,
				enableGroupedSearch: true,
				allowSearchFiltersMutation: true,
				hiddenParameters: [
					'searchId',
					'pageId',
					'url',
					'siteId',
					'cultureId',
				],
				searchResponseTransformerMethod,
				dataMergerMethod,
				limit: {
					1: { initial: 6, value: 6 },
					2: { initial: 3, value: 6 },
					3: { initial: 3, value: 6 },
					10: { initial: 3, value: 6 },
				},
			}"
			class="pt-40 >=768:pt-80 relative"
			@update="onUpdate"
		>
			<!-- Lens flare -->
			<Transition name="t-search-page-custom__flares">
				<div
					v-if="formattedGroups.length && onSearchPage"
					:class="[
						'h-full overflow-hidden pointer-events-none',
						'absolute top-0 -left-layout-margin -right-layout-margin',
					]"
				>
					<LensFlare
						id="search-flare-01"
						:angle="45"
						:flares="[
							{
								size: 1000,
								offset: -200,
								theme: 'primary',
								opacity: 1,
							},
							{
								size: 450,
								offset: 200,
								theme: 'secondary',
								opacity: 0.8,
							},
						]"
						:class="[
							'c-search-page-custom__lensflare',
							'absolute top-0 max-w-layout-max w-full',
						]"
					/>

					<LensFlare
						v-if="!state.isLoading"
						id="search-flare-02"
						:angle="225"
						:flares="[
							{
								size: 1000,
								offset: -200,
								theme: 'primary',
								opacity: 1,
							},
							{
								size: 450,
								offset: 200,
								theme: 'secondary',
								opacity: 0.8,
							},
						]"
						:class="[
							'c-search-page-custom__lensflare',
							'absolute bottom-0 max-w-layout-max w-full',
						]"
					/></div
			></Transition>

			<!-- Loader -->
			<TransitionExt name="fade-in-out" duration="300">
				<div
					v-if="state.isLoading && state.isInitiated"
					class="absolute top-0 left-0 w-full h-full"
				>
					<LoaderGraphic
						:class="[
							'sticky top-1/2 mx-auto z-50',
							'transform -translate-y-1/2',
						]"
					/>
				</div>
			</TransitionExt>

			<!-- Groups -->
			<Transition
				name="t-search-page-custom__groups"
				mode="out-in"
				appear
			>
				<div
					:key="key"
					:class="[
						'flex flex-col gap-y-3xl >=1024:gap-y-4xl',
						'pt-4xl pb-120 >=768:pb-220',
					]"
				>
					<div
						v-for="(infobox, index) in formattedInfoboxes"
						:key="`infobox-${index}`"
						:class="[
							'flex flex-wrap justify-center gap-layout-gutter',
						]"
					>
						<div
							v-for="(section, sindex) in infobox"
							:key="`infobox-section-${sindex}`"
							:class="[
								'border-2 border-opacity-20 border-text',
								'font-darker-grotesque text-search-infobox font-medium',
								'w-full px-xl pt-xl pb-2xl',
								'>=1024:w-6/12col >=1024:px-3xl >=1024:py-xl',
							]"
							v-html="section.text"
						></div>
					</div>

					<div
						v-for="(group, gindex) in formattedGroups"
						:key="`group-${gindex}`"
					>
						<BaseH3 tag="h2" class="mb-lg">
							<span class="mr-6" v-text="group.name"></span>
							<span
								class="opacity-70"
								v-text="group.total"
							></span>
						</BaseH3>

						<Component
							:is="getComponentFromType(group.type)"
							v-if="getComponentFromType(group.type)"
							:id="group.targetId"
							:items="group.items"
						/>

						<button
							v-if="group.limit + group.offset < group.total"
							:class="[
								'mx-auto max-w-fit flex items-center gap-x-12 mt-lg',
								'uppercase font-darker-grotesque font-bold text-button-xs',
							]"
							@click="() => action.fetchMore(group.id)"
						>
							<span v-text="'Vis flere'"></span>
							<SvgIconCaret class="w-14 mt-3" />
						</button>
					</div>

					<TransitionExt name="fade-in-out" duration="500">
						<div
							v-if="
								formattedGroups &&
								!formattedGroups.length &&
								computedFields.text &&
								computedFields.text.value.length &&
								!state.isLoading &&
								!formattedInfoboxes.length
							"
							:class="[
								'font-darker-grotesque font-medium',
								'text-h4 text-center',
							]"
						>
							<span v-text="'Ingen resultater fundet'"></span>
						</div>
					</TransitionExt>
				</div>
			</Transition>
		</LimboSearch>
	</article>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import globalEventBus from '~/assets/js/global-event-bus';
import SvgIconCaret from '~/assets/svgs/icon-caret.svg?inline';
import LimboSearch from '~/citi-baseline/components/LimboSearch';
import LensFlare from '~/components/shared/LensFlare';
import LoaderGraphic from '~/components/shared/LoaderGraphic';
import {
	SearchSectionPages,
	SearchSectionSelfServices,
	SearchSectionNews,
	SearchSectionDocuments,
} from '~/components/shared/SearchSections';

export default {
	name: 'MainSearchPageCustom',

	components: {
		SvgIconCaret,
		LimboSearch,
		LensFlare,
		LoaderGraphic,
		SearchSectionPages,
		SearchSectionSelfServices,
		SearchSectionNews,
		SearchSectionDocuments,
	},

	inheritAttrs: false,

	props: {
		title: String,
		search: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		const fields = this.search.fields.map((field) =>
			Object.assign(field, {
				value: this.$route.query[field.name] ?? field.value ?? '',
			})
		);

		return {
			filters: {
				...this.search,
				fields,
			},

			infoboxes: [],
			groups: [],
		};
	},

	computed: {
		computedHiddenFields() {
			return this.filters?.fields?.filter(
				(field) => field.type === 'hidden'
			);
		},

		computedFields() {
			return this.filters?.fields
				?.filter((field) => field.type !== 'hidden')
				.reduce((acc, cur) => ({ ...acc, [cur.name]: cur }), {});
		},

		formattedGroups() {
			return (
				this.groups
					?.filter((group) => group.total)
					.map((group) => ({
						...group,
						targetId: `search-target-group-${group.id}`,
					})) ?? []
			);
		},

		formattedInfoboxes() {
			return this.infoboxes?.map(
				(box) =>
					[
						{ text: box.text1, link: box.link1 },
						{ text: box.text2, link: box.link2 },
					].filter((section) => section.text || section.link) ?? []
			);
		},

		key() {
			const key = this.groups
				.filter((group) => group.total)
				.map((group) => group.total);

			return `searchpage-${key}`;
		},

		...mapState({
			onSearchPage: (state) => state.template === 'SearchPage',
			url: (state) => state.previousPath ?? state.currentPath,
			pageId: (state) => state.previousPageId ?? state.pageId,
			searchId: (state) => state.MainSearchPage.searchId,
		}),
	},

	mounted() {
		this.generateSearchId();

		globalEventBus.$on('globalSearch:request', (searchText) => {
			this.generateSearchId();

			this.$set(
				this.filters,
				'fields',
				this.filters.fields?.map((field) => ({
					...field,
					value: field.name === 'text' ? searchText : field.value,
				}))
			);
		});
	},

	beforeDestroy() {
		globalEventBus.$off('globalSearch:request');
		this.$store.commit('MainSearchPage/SET_DATA', {
			infoboxes: [],
			groups: [],
		});
	},

	methods: {
		onUpdate(event) {
			this.infoboxes = event.data?.infoboxes ?? [];
			this.groups = (event.data?.groups ?? []).map((group) => ({
				targetId: `search-target-group-${group.id}`,
				...group,
			}));

			this.$store.commit('MainSearchPage/SET_DATA', {
				infoboxes: this.infoboxes,
				groups: this.groups,
			});
		},

		getComponentFromType(type) {
			return {
				pages: 'SearchSectionPages',
				selfservice: 'SearchSectionSelfServices',
				news: 'SearchSectionNews',
				documents: 'SearchSectionDocuments',
			}[type];
		},

		searchResponseTransformerMethod(value) {
			const pagination = {};
			const data = value?.data || {};

			data?.groups?.forEach?.((group) => {
				const { id, limit, offset, total } = group;
				pagination[id] = { limit, offset, total };
			});

			return { ...value, data: { data, pagination } };
		},

		dataMergerMethod(newData, oldData) {
			if (newData && oldData) {
				const compiledGroups = [];
				oldData.groups.forEach((group) => {
					const newGroup = newData.groups.find((newGroup) => {
						return newGroup.id === group.id;
					});

					if (newGroup) {
						newGroup.items = [...group.items, ...newGroup.items];
						compiledGroups.push(newGroup);
						return;
					}

					compiledGroups.push(group);
				});

				return {
					infoboxes: newData.infoboxes || oldData.infoboxes,
					groups: compiledGroups,
				};
			}

			return newData || Object.assign({}, oldData);
		},

		...mapActions('MainSearchPage', {
			generateSearchId: 'SET_SEARCH_ID',
		}),
	},
};
</script>

<style lang="postcss">
.t-search-page-custom__groups-enter-active {
	@apply duration-300 ease-smooth-out transform  delay-200;
}
.t-search-page-custom__groups-leave-active {
	@apply duration-300 ease-smooth-in transform;
}
.t-search-page-custom__groups-enter,
.t-search-page-custom__groups-leave-to {
	@apply translate-y-80 opacity-0;
}

.d-search-page-custom,
.d-search-page-custom .c-limbo-search {
	min-height: calc(100vh - var(--theme-spacing-site-header));
}

.c-search-page-custom__lensflare:nth-child(1) {
	transform: translateY(calc(-100% + 14vw + 80px));
}
.c-search-page-custom__lensflare:nth-child(2) {
	transform: translateY(calc(100% - 14vw - 40px));
}

/**
	Lens flare transition
 */
.t-search-page-custom__flares-enter-active,
.t-search-page-custom__flares-leave-active,
.t-search-page-custom__flares-enter-active > *,
.t-search-page-custom__flares-leave-active > * {
	@apply duration-300 ease-smooth-out;
}
.t-search-page-custom__flares-enter,
.t-search-page-custom__flares-leave-to {
	@apply opacity-0;
}
.t-search-page-custom__flares-enter > *:nth-child(1),
.t-search-page-custom__flares-leave-to > *:nth-child(1) {
	transform: translate3d(0, -100%, 0);
}
.t-search-page-custom__flares-enter > *:nth-child(2),
.t-search-page-custom__flares-leave-to > *:nth-child(2) {
	transform: translate3d(0, 100%, 0);
}
</style>
